import { usePageTitle } from '@/hooks/usePageTitle';
import { PageHeader } from '@/components/ui/page-header';
import {
  ArrowRight,
  CircleDot,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Smartphone,
  Monitor,
  ClipboardList,
  PackageCheck,
  Send,
  Lock,
  Ban,
  Pencil,
  Users,
  Undo2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

/* ── Inline helpers ── */

function TocLink({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <button
      onClick={() =>
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
      }
      className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
    >
      {children}
    </button>
  );
}

function InfoCallout({
  variant,
  children,
}: {
  variant: 'info' | 'warning' | 'danger';
  children: React.ReactNode;
}) {
  const styles = {
    info: 'border-info/30 bg-info/10',
    warning: 'border-warning/30 bg-warning/10',
    danger: 'border-destructive/50 bg-destructive/5',
  };
  return (
    <div className={cn('rounded-md border p-4 text-sm', styles[variant])}>
      {children}
    </div>
  );
}

interface LifecycleStepProps {
  number: number;
  title: string;
  subtitle: string;
  colorClasses: string;
  isLast?: boolean;
}

function LifecycleStep({
  number,
  title,
  subtitle,
  colorClasses,
  isLast = false,
}: LifecycleStepProps) {
  return (
    <div className="flex-1 flex flex-col items-center relative">
      {!isLast && (
        <div className="absolute top-5 left-1/2 w-full h-0.5 bg-border" />
      )}
      <div
        className={cn(
          'relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 text-sm font-bold bg-card',
          colorClasses
        )}
      >
        {number}
      </div>
      <span className="mt-2 text-sm font-medium text-center">{title}</span>
      <span className="text-xs text-muted-foreground text-center mt-1">
        {subtitle}
      </span>
    </div>
  );
}

/* ── Main component ── */

export function PicklistGuidePage() {
  usePageTitle('Picklist Guide');
  return (
    <div className="scroll-smooth space-y-6">
      {/* Page header — the shared primitive, not a hand-rolled icon + h1 + p. */}
      <PageHeader
        title="Picklist Guide"
        description="How picklists work in MRO issuance — and why picked is not the same as posted."
      />

      {/* Two-column layout */}
      <div className="flex gap-6">
        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-8">
          {/* ── Section 1: Overview ── */}
          <section id="overview">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CircleDot className="h-5 w-5" />
                  Overview
                </CardTitle>
                <CardDescription>
                  What is a picklist and how does it fit into the MRO workflow
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm leading-relaxed">
                  A <strong>picklist</strong> is a document that groups material
                  request items together for efficient warehouse picking and
                  issuance. It is the central workflow document that connects
                  material requests (MRS) from NetSuite to the actual picking
                  and posting process. Each picklist has one or more assigned{' '}
                  <strong>pickers</strong> — users responsible for fulfilling the
                  items. The creator is automatically assigned as the first
                  picker, and additional pickers can be added or removed.
                </p>
                <InfoCallout variant="info">
                  <strong>Creator vs Picker roles:</strong> The picklist creator
                  has broader control — they can <strong>edit items</strong>{' '}
                  (add/remove) and <strong>cancel</strong> the picklist. Regular
                  assigned pickers can <strong>pick</strong>,{' '}
                  <strong>post MIS</strong>, and <strong>close</strong> the
                  picklist, but cannot edit items or cancel. Users with the{' '}
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                    view_all_picklists
                  </code>{' '}
                  permission (supervisors) can perform all actions on any
                  picklist regardless of assignment.
                </InfoCallout>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 rounded-md border p-4">
                    <Monitor className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">
                        Warehouse Supervisor (Web)
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Creates picklists, monitors progress, posts to NetSuite
                        (MIS), closes and cancels picklists.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 rounded-md border p-4">
                    <Smartphone className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">
                        Warehouse Picker (Mobile)
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Executes picks on the warehouse floor via barcode
                        scanning or manual input on the mobile app.
                      </p>
                    </div>
                  </div>
                </div>
                <InfoCallout variant="info">
                  <strong>Dual-status system:</strong> Each picklist tracks two
                  independent statuses — <strong>Document Status</strong>{' '}
                  (controls which actions are allowed) and{' '}
                  <strong>Picklist Status</strong> (tracks picking progress).
                  These are explained in detail in the{' '}
                  <button
                    onClick={() =>
                      document
                        .getElementById('status-reference')
                        ?.scrollIntoView({ behavior: 'smooth' })
                    }
                    className="underline font-medium"
                  >
                    Status Reference
                  </button>{' '}
                  section below.
                </InfoCallout>
              </CardContent>
            </Card>
          </section>

          {/* ── Section 2: Lifecycle Flow ── */}
          <section id="lifecycle">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRight className="h-5 w-5" />
                  Lifecycle Flow
                </CardTitle>
                <CardDescription>
                  How a picklist moves from creation to completion
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Main flow stepper */}
                <div>
                  <p className="text-sm font-medium mb-4">
                    Main Flow (Happy Path)
                  </p>
                  <div className="flex items-start overflow-x-auto pb-2">
                    <LifecycleStep
                      number={1}
                      title="Creation"
                      subtitle="Document: Open"
                      colorClasses="border-info text-info"
                    />
                    <LifecycleStep
                      number={2}
                      title="Picking"
                      subtitle="Progress tracked"
                      colorClasses="border-warning text-warning"
                    />
                    <LifecycleStep
                      number={3}
                      title="Posting (MIS)"
                      subtitle="Items → NetSuite"
                      colorClasses="border-info text-info"
                    />
                    <LifecycleStep
                      number={4}
                      title="Closed"
                      subtitle="Finalized"
                      colorClasses="border-success text-success"
                      isLast
                    />
                  </div>
                </div>

                <Separator />

                {/* Step descriptions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-md border p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="default">1</Badge>
                      <span className="text-sm font-medium">Creation</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Select pending MRS items, set quantities to pick. System
                      performs smart bin allocation using FIFO logic. Picklist is
                      created with <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge> status
                      and <Badge variant="secondary" className="text-xs px-1.5 py-0">Pending Fulfillment</Badge>.
                    </p>
                  </div>
                  <div className="rounded-md border p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="warning">2</Badge>
                      <span className="text-sm font-medium">Picking</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Warehouse pickers use the mobile app to scan bins and
                      record picked quantities. Picklist status transitions:{' '}
                      <Badge variant="secondary" className="text-xs px-1.5 py-0">Pending</Badge>{' '}
                      <ArrowRight className="inline h-3 w-3" />{' '}
                      <Badge variant="warning" className="text-xs px-1.5 py-0">Partial</Badge>{' '}
                      <ArrowRight className="inline h-3 w-3" />{' '}
                      <Badge variant="success" className="text-xs px-1.5 py-0">Fulfilled</Badge>.
                    </p>
                  </div>
                  <div className="rounded-md border p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-info">3</Badge>
                      <span className="text-sm font-medium">
                        Posting (MIS)
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Post picked items to NetSuite as an inventory adjustment
                      (Material Issuance Slip). This increments the MRS item's
                      fulfilled quantity. If all items are fully picked and
                      posted, the picklist auto-closes.
                    </p>
                  </div>
                  <div className="rounded-md border p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="success">4</Badge>
                      <span className="text-sm font-medium">Closed</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      The picklist is finalized. No further picking, posting, or
                      editing is allowed. Can happen automatically (all items
                      posted) or manually (supervisor closes with optional
                      reason).
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Cancellation branch */}
                <div>
                  <p className="text-sm font-medium mb-3">
                    Cancellation Branch
                  </p>
                  <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-4">
                    <Ban className="h-5 w-5 text-destructive shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Cancel Picklist</p>
                      <p className="text-xs text-muted-foreground">
                        Available when the picklist is{' '}
                        <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>{' '}
                        and <strong>no items have been posted to NetSuite</strong>.
                        If items were picked but not posted, a supervisor must
                        void them first. Voids the document and releases all
                        items back to the available pool. Requires a reason.
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* ── Section 3: Status Reference ── */}
          <section id="status-reference">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Status Reference
                </CardTitle>
                <CardDescription>
                  Understanding the dual-status system
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Document Status */}
                <div>
                  <h3 className="text-sm font-semibold mb-3">
                    Document Status
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Controls which workflow actions are allowed on the picklist.
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-36">Status</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-44">
                          Allowed Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Badge variant="default">Open</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          Active picklist. This is the initial status when a
                          picklist is created. Available for picking, posting,
                          editing, closing, and cancellation.
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          Edit, Pick, Post, Close, Cancel
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Badge variant="success">Closed</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          Finalized. No further actions allowed. Reached via
                          auto-close (all items posted) or manual close.
                          At least one item must have been posted to NetSuite
                          before a picklist can be closed.
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          View only
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Badge variant="destructive">Cancelled</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          Voided. All items released back to the available pool.
                          Allowed even after picking, as long as no items have
                          been posted and all picked items have been voided by a
                          supervisor. Cannot be undone.
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          View only
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Picklist Status */}
                <div>
                  <h3 className="text-sm font-semibold mb-3">
                    Picklist Status (Fulfillment Progress)
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Tracks how much of the picklist has been picked. Updated
                    automatically as picks are recorded.
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-44">Status</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-48">Condition</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Badge variant="secondary">
                            Pending Fulfillment
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          No items have been picked yet.
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          All items: Qty Picked = 0
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Badge variant="warning">
                            Partially Fulfilled
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          Some items have been picked, but not all are complete.
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          At least one item picked, not all fully picked
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Badge variant="success">Fulfilled</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          All items have been fully picked (Qty Picked &ge; Qty
                          to Pick for every item).
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          All items: IsPicked = true
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Actions Matrix */}
                <div>
                  <h3 className="text-sm font-semibold mb-3">
                    Actions Matrix
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Which actions are allowed based on the current status
                    combination.
                  </p>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-40">Action</TableHead>
                          <TableHead className="w-24">Role</TableHead>
                          <TableHead className="w-24">
                            Doc Status
                          </TableHead>
                          <TableHead>Additional Conditions</TableHead>
                          <TableHead className="w-44">
                            Permission
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="text-sm font-medium">
                            Edit (add/remove items)
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs px-1.5 py-0">Creator</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            No picking has started on any item
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              edit_picklist
                            </code>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-sm font-medium">
                            Record Pick
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs px-1.5 py-0">Picker</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            Item not fully picked; quantity within bin allocation
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              fulfill_picklist
                            </code>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-sm font-medium">
                            Post to NetSuite
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs px-1.5 py-0">Picker</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            Item has picks recorded; item not already posted
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              post_picklist
                            </code>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-sm font-medium">
                            Close
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs px-1.5 py-0">Picker</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            At least one item must be posted; all picked items
                            must be posted first; reason required if partial
                            fulfillment
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              close_picklist
                            </code>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-sm font-medium">
                            Manage Pickers
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground">Any</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            Target user must have fulfill_picklist permission; at least one picker must remain
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              manage_picklist_pickers
                            </code>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-sm font-medium">
                            Adjust
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground">Picker or manager</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="px-1.5 py-0 text-xs">Open</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            Reduce-only, per bin, above the posted floor. Reason required. Refused while a
                            post is in flight or after a failure that may have reached NetSuite.
                          </TableCell>
                          <TableCell>
                            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                              issuance.adjust_picklist
                            </code>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-sm font-medium">
                            Cancel
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs px-1.5 py-0">Creator</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            No items posted to NetSuite; no unvoided picked
                            items (supervisor must void first); reason required
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              cancel_picklist
                            </code>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                  <InfoCallout variant="info">
                    <strong>Role column:</strong>{' '}
                    <Badge variant="secondary" className="text-xs px-1.5 py-0">Creator</Badge>{' '}
                    means only the user who created the picklist can perform the action.{' '}
                    <Badge variant="outline" className="text-xs px-1.5 py-0">Picker</Badge>{' '}
                    means any assigned picker (including the creator, who is always a picker) can perform the action.
                    Users with{' '}
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      view_all_picklists
                    </code>{' '}
                    (supervisors) bypass role restrictions and can perform all actions.
                  </InfoCallout>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* ── Section 4: Actions Guide ── */}
          <section id="actions-guide">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PackageCheck className="h-5 w-5" />
                  Actions Guide
                </CardTitle>
                <CardDescription>
                  Step-by-step instructions for each picklist action
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Creating a Picklist */}
                <div id="action-create" className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold">
                      Creating a Picklist
                    </h3>
                    <Badge variant="default" className="text-xs px-1.5 py-0">Web</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Create a new picklist by selecting pending material request
                    items and setting the quantities to pick.
                  </p>
                  <div className="text-sm space-y-1.5 pl-4">
                    <p>
                      1. Navigate to <strong>Picklists</strong> and click{' '}
                      <strong>Create Picklist</strong>.
                    </p>
                    <p>
                      2. Select material request items from the available pool.
                      The system shows stock availability and pending quantities.
                    </p>
                    <p>3. Set the quantity to pick for each selected item.</p>
                    <p>4. Optionally add notes for the pickers.</p>
                    <p>
                      5. Click <strong>Create</strong>. The system performs smart
                      bin allocation using FIFO logic and auto-assigns you as
                      the first picker.
                    </p>
                  </div>
                  <InfoCallout variant="info">
                    Items committed to a picklist reduce the available-to-pick
                    quantity for other picklists. The quantity is not released
                    until the picklist is cancelled or the item is removed.
                  </InfoCallout>
                </div>

                <Separator />

                {/* Recording Picks */}
                <div id="action-pick" className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-warning" />
                    <h3 className="text-sm font-semibold">Recording Picks</h3>
                    <Badge variant="warning" className="text-xs px-1.5 py-0">Mobile</Badge>
                    <Badge variant="outline" className="text-xs px-1.5 py-0">Assigned pickers</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Assigned pickers use the mobile app to record picked
                    quantities from allocated bins. Only users assigned as
                    pickers on the picklist can record picks.
                  </p>
                  <div className="text-sm space-y-1.5 pl-4">
                    <p>
                      1. Open the picklist on the mobile app from the{' '}
                      <strong>Pick</strong> tab.
                    </p>
                    <p>
                      2. Tap a <strong>bin header</strong> to expand and set it
                      as the active bin.
                    </p>
                    <p>
                      3. Tap an <strong>item row</strong> to open the quantity
                      input dialog.
                    </p>
                    <p>4. Enter the picked quantity and confirm.</p>
                    <p>
                      5. Repeat for each item/bin. Progress updates
                      automatically.
                    </p>
                  </div>
                  <InfoCallout variant="info">
                    When the same item code appears in multiple MRS lines within
                    the same bin, the system automatically targets the first
                    unfulfilled item and overflows excess quantities to the next
                    one.
                  </InfoCallout>
                </div>

                <Separator />

                {/* Posting to NetSuite */}
                <div id="action-post" className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Send className="h-4 w-4 text-info" />
                    <h3 className="text-sm font-semibold">
                      Posting to NetSuite (MIS)
                    </h3>
                    <Badge variant="default" className="text-xs px-1.5 py-0">Web</Badge>
                    <Badge variant="outline" className="text-xs px-1.5 py-0">Assigned pickers</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Post picked items to NetSuite as an inventory adjustment
                    record (Material Issuance Slip). Only assigned pickers can
                    post.
                  </p>
                  <div className="text-sm space-y-1.5 pl-4">
                    <p>
                      1. Open the picklist detail page and click{' '}
                      <strong>Post MIS</strong>.
                    </p>
                    <p>
                      2. Select which picked items to include in this posting.
                    </p>
                    <p>
                      3. Choose an <strong>adjustment reason</strong> (required).
                    </p>
                    <p>4. Optionally add per-item memos.</p>
                    <p>
                      5. Click <strong>Post</strong>. The system creates an
                      inventory adjustment in NetSuite.
                    </p>
                  </div>
                  <InfoCallout variant="warning">
                    <strong>Auto-close:</strong> If all items on the picklist are
                    fully picked and posted after this action, the picklist will
                    automatically close. No manual close is needed.
                  </InfoCallout>
                </div>

                <Separator />

                {/* Adjusting a recorded pick */}
                <div id="action-adjust" className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Undo2 className="h-4 w-4 text-warning" />
                    <h3 className="text-sm font-semibold">Adjusting a recorded pick</h3>
                    <Badge variant="default" className="px-1.5 py-0 text-xs">Web</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Correct what was recorded as picked, before it reaches NetSuite. Adjust works on one item, per
                    bin and MRS line: it can lower a pick, unpick a serial, or move units to another planned MRS
                    line of the same item that still has room. It never raises a bin above what was picked in it
                    and never moves units between bins. Every change is audited with a reason. Nothing is reversed
                    in NetSuite, because nothing has moved there yet.
                  </p>
                  <div className="space-y-2 text-sm">
                    <p className="font-medium">Prerequisites:</p>
                    <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                      <li>
                        The picklist is{' '}
                        <Badge variant="default" className="px-1.5 py-0 text-xs">Open</Badge> — a closed or
                        cancelled picklist is frozen.
                      </li>
                      <li>
                        You have the{' '}
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">issuance.adjust_picklist</code>{' '}
                        permission, and you are an assigned picker or a manager.
                      </li>
                      <li>The item has picked quantity that is not already posted.</li>
                      <li>
                        <strong>No post is in flight.</strong> While any batch is posting — or after one
                        failed in a way that may already have reached NetSuite — the whole picklist is
                        frozen until it settles or is reconciled.
                      </li>
                      <li>
                        <strong>No posting batch holds units of the item.</strong> If a failed batch still
                        counts on them, retry or cancel that batch first; lowering its units would make every
                        retry fail again.
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-1.5 pl-4 text-sm">
                    <p>1. Open the item's row on the picklist detail page and press <strong>Adjust</strong>.</p>
                    <p>2. Pick a reason. Notes are required when the reason is <em>Other</em>.</p>
                    <p>
                      3. Each bin is a card, with one row per MRS line picked there. A row can only be lowered,
                      and never below what it has already posted. A partly posted row says how much of it can
                      still change.
                    </p>
                    <p>
                      4. Units picked for the wrong MRS? Press a row's <strong>move</strong> icon. It opens its own
                      dialog: choose the MRS line that has room, set how many, and press <strong>Move</strong>. It
                      saves at once and asks no reason — nothing physical changes, only the MRS the units count
                      for. It is not offered while you have unapplied changes in Adjust.
                    </p>
                    <p>
                      5. Units you lowered leave the pick: say where they went —{' '}
                      <strong>Returned to bin</strong>, <strong>Damaged</strong> or <strong>Missing</strong>.
                      Damaged and Missing units still count in NetSuite until the stock is adjusted there.
                    </p>
                    <p>
                      6. For a serialized item, each serial is <strong>Keep</strong> or <strong>Unpick</strong>;{' '}
                      <strong>Move…</strong> opens the same Move dialog. Then press{' '}
                      <strong>Apply adjustment</strong>. There is no preview step.
                    </p>
                  </div>
                  <InfoCallout variant="info">
                    <strong>Wrong bin?</strong> The bin of a pick cannot be changed here. Lower it in the wrong
                    bin with <em>Returned to bin</em>, then pick it again from the right bin on the handheld.
                  </InfoCallout>
                  <InfoCallout variant="info">
                    <strong>Serials are never swapped in Adjust.</strong> When another picklist took a serial
                    this picklist had reserved, the item's bin card shows it and offers <strong>Swap serial</strong>,
                    which gives the slot another serial; the picker can also swap it on the handheld.
                  </InfoCallout>
                  <InfoCallout variant="warning">
                    <strong>Adjust is not the same as posting less.</strong> Posting less leaves the recorded
                    pick intact and the remainder can go on a later MIS. Adjust <em>reduces the recorded
                    pick</em> and permanently writes a reason and an audit row. Use Adjust only when the
                    record is wrong about what was taken off the shelf.
                  </InfoCallout>
                </div>

                <Separator />

                {/* Closing a Picklist */}
                <div id="action-close" className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-success" />
                    <h3 className="text-sm font-semibold">
                      Closing a Picklist
                    </h3>
                    <Badge variant="default" className="text-xs px-1.5 py-0">Web</Badge>
                    <Badge variant="outline" className="text-xs px-1.5 py-0">Assigned pickers</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Manually finalize a picklist when you want to close it
                    before all items are fully picked (partial fulfillment).
                    Any assigned picker can close.
                  </p>
                  <div className="text-sm space-y-2">
                    <p className="font-medium">Prerequisites:</p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>
                        Document status must be{' '}
                        <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>
                      </li>
                      <li>At least one item must have been posted to NetSuite</li>
                      <li>All picked items must be posted to NetSuite first</li>
                      <li>
                        If any items have unpicked quantities, a{' '}
                        <strong>closing reason</strong> is required
                      </li>
                    </ul>
                  </div>
                  <div className="text-sm space-y-1.5 pl-4">
                    <p>
                      1. On the picklist detail page, click{' '}
                      <strong>Close</strong>.
                    </p>
                    <p>
                      2. If partial fulfillment, enter a reason explaining why
                      items were not fully picked.
                    </p>
                    <p>
                      3. Confirm. The picklist moves to{' '}
                      <Badge variant="success" className="text-xs px-1.5 py-0">Closed</Badge>{' '}
                      status.
                    </p>
                  </div>
                  <InfoCallout variant="warning">
                    <strong>Unposted picks block closing.</strong> If any items
                    have been picked but not yet posted to NetSuite, you must
                    post them first before closing. If nothing has been posted
                    yet, use Cancel instead.
                  </InfoCallout>
                </div>

                <Separator />

                {/* Cancelling a Picklist */}
                <div id="action-cancel" className="space-y-3">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-destructive" />
                    <h3 className="text-sm font-semibold">
                      Cancelling a Picklist
                    </h3>
                    <Badge variant="default" className="text-xs px-1.5 py-0">Web</Badge>
                    <Badge variant="secondary" className="text-xs px-1.5 py-0">Creator only</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Void a picklist entirely, releasing all items back to the
                    available pool. Only the picklist creator or a supervisor
                    can cancel.
                  </p>
                  <div className="text-sm space-y-2">
                    <p className="font-medium">Prerequisites:</p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>
                        You must be the <strong>picklist creator</strong> or a <strong>supervisor</strong>
                      </li>
                      <li>
                        Document status must be{' '}
                        <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>
                      </li>
                      <li>
                        <strong>
                          No items posted to NetSuite
                        </strong>
                      </li>
                      <li>
                        All picked items must be <strong>voided by a supervisor</strong> before cancelling
                      </li>
                    </ul>
                  </div>
                  <div className="text-sm space-y-1.5 pl-4">
                    <p>
                      1. On the picklist detail page, click{' '}
                      <strong>Cancel</strong>.
                    </p>
                    <p>
                      2. Enter a cancellation reason (required, max 500
                      characters).
                    </p>
                    <p>
                      3. Confirm. The picklist moves to{' '}
                      <Badge variant="destructive" className="text-xs px-1.5 py-0">Cancelled</Badge>{' '}
                      status.
                    </p>
                  </div>
                  <InfoCallout variant="danger">
                    <strong>This action cannot be undone.</strong> All picklist
                    items are permanently removed, and their quantities are
                    released back to the available pool for new picklists. If
                    items were picked but not posted, a supervisor must void
                    them first. If any items have already been posted to
                    NetSuite, use Close instead.
                  </InfoCallout>
                </div>

                <Separator />

                {/* Editing a Picklist */}
                <div id="action-edit" className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Pencil className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold">
                      Editing a Picklist (Add/Remove Items)
                    </h3>
                    <Badge variant="default" className="text-xs px-1.5 py-0">Web</Badge>
                    <Badge variant="secondary" className="text-xs px-1.5 py-0">Creator only</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Add new MRS items to or remove existing items from a
                    picklist before picking begins. Only the picklist creator
                    or a supervisor can edit items.
                  </p>
                  <div className="text-sm space-y-2">
                    <p className="font-medium">Prerequisites:</p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>
                        You must be the <strong>picklist creator</strong> or a <strong>supervisor</strong>
                      </li>
                      <li>
                        Document status must be{' '}
                        <Badge variant="default" className="text-xs px-1.5 py-0">Open</Badge>
                      </li>
                      <li>
                        No picking has started on <strong>any</strong> item in
                        the picklist
                      </li>
                    </ul>
                  </div>
                  <div className="text-sm space-y-1.5 pl-4">
                    <p>
                      <strong>Adding items:</strong> Click{' '}
                      <strong>Add Items</strong> on the detail page. Select from
                      available MRS items (items already on this picklist are
                      excluded).
                    </p>
                    <p>
                      <strong>Removing items:</strong> Click the trash icon next
                      to an item in the items table. Confirm the removal.
                    </p>
                  </div>
                  <InfoCallout variant="warning">
                    A picklist must have at least one item. If you need to remove
                    all items, cancel the picklist instead.
                  </InfoCallout>
                </div>

                <Separator />

                {/* Managing Pickers */}
                <div id="action-pickers" className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold">
                      Managing Pickers
                    </h3>
                    <Badge variant="default" className="text-xs px-1.5 py-0">Web</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Assign or remove warehouse pickers responsible for fulfilling
                    items on a picklist. Assigned pickers can view and pick items
                    for the picklist on the mobile app.
                  </p>
                  <div className="text-sm space-y-2">
                    <p className="font-medium">Key rules:</p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>
                        The picklist creator is <strong>automatically assigned</strong>{' '}
                        as the first picker when the picklist is created
                      </li>
                      <li>
                        Only users with the{' '}
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          fulfill_picklist
                        </code>{' '}
                        permission can be assigned as pickers
                      </li>
                      <li>
                        A picklist must always have <strong>at least one picker</strong>{' '}
                        — the last picker cannot be removed
                      </li>
                      <li>
                        Pickers cannot be modified on{' '}
                        <Badge variant="success" className="text-xs px-1.5 py-0">Closed</Badge>{' '}
                        or{' '}
                        <Badge variant="destructive" className="text-xs px-1.5 py-0">Cancelled</Badge>{' '}
                        picklists
                      </li>
                    </ul>
                  </div>
                  <div className="text-sm space-y-1.5 pl-4">
                    <p>
                      1. On the picklist detail page, click{' '}
                      <strong>Manage Pickers</strong>.
                    </p>
                    <p>
                      2. To add a picker, select a user from the dropdown and click
                      the add button. Only eligible users (active, with fulfill
                      permission) are shown.
                    </p>
                    <p>
                      3. To remove a picker, click the <strong>X</strong> button
                      next to their name.
                    </p>
                  </div>
                  <InfoCallout variant="info">
                    <strong>Access scoping:</strong> Assigned pickers can see the
                    picklist in their picklist list even if they didn&apos;t create it.
                    Users without the{' '}
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      view_all_picklists
                    </code>{' '}
                    permission will only see picklists they created or are assigned
                    to as pickers.
                  </InfoCallout>
                  <InfoCallout variant="warning">
                    <strong>Role distinction:</strong> The creator (who is always a
                    picker) can edit items and cancel the picklist. Regular assigned
                    pickers can only pick, post MIS, and close. Both roles require
                    the corresponding permission to perform each action. Supervisors
                    with{' '}
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      view_all_picklists
                    </code>{' '}
                    can perform all actions on any picklist.
                  </InfoCallout>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* ── Section 5: Permissions Reference ── */}
          <section id="permissions">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Permissions Reference
                </CardTitle>
                <CardDescription>
                  Required permissions for each picklist action. Contact your
                  administrator to request access.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-48">Action</TableHead>
                      <TableHead className="w-56">
                        Permission Code
                      </TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        View picklists
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          issuance.view_picklist
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        View picklists you created or are assigned to as a picker
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        Read MRS released quantities
                      </TableCell>
                      <TableCell>
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          issuance.view_request_list
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        Not a picklist action, but a picklist precondition: the Post dialog reads how much
                        the MRS has released through this permission. A poster who can post but cannot read
                        the request list still posts — the server checks the MRS ceiling — but the released
                        figures in the dialog stay blank and say so.
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        View all picklists
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          issuance.view_all_picklists
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        View picklists created by any user (supervisor level)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        Create picklist
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          issuance.create_picklist
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        Create new picklists from pending MRS items
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        Edit picklist
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          issuance.edit_picklist
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        Add or remove items from a picklist
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        Record picks
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          issuance.fulfill_picklist
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        Submit pick quantities on the mobile app
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        Post to NetSuite (MIS)
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          issuance.post_picklist
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        Post picked items as inventory adjustments to NetSuite
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        Close picklist
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          issuance.close_picklist
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        Finalize and close a picklist
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        Cancel picklist
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          issuance.cancel_picklist
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        Void a picklist and release all items
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-sm font-medium">
                        Manage pickers
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          issuance.manage_picklist_pickers
                        </code>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        Add or remove pickers from a picklist
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </section>
        </div>

        {/* Sticky Table of Contents */}
        <div className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">On this page</CardTitle>
              </CardHeader>
              <CardContent className="space-y-0.5">
                <TocLink id="overview">Overview</TocLink>
                <TocLink id="lifecycle">Lifecycle Flow</TocLink>
                <TocLink id="status-reference">Status Reference</TocLink>
                <TocLink id="actions-guide">Actions Guide</TocLink>
                <div className="pl-3 space-y-0.5">
                  <TocLink id="action-create">Creating a Picklist</TocLink>
                  <TocLink id="action-pick">Recording Picks</TocLink>
                  <TocLink id="action-post">Posting (MIS)</TocLink>
                  <TocLink id="action-adjust">Adjusting a Pick</TocLink>
                  <TocLink id="action-close">Closing</TocLink>
                  <TocLink id="action-cancel">Cancelling</TocLink>
                  <TocLink id="action-edit">Editing</TocLink>
                  <TocLink id="action-pickers">Managing Pickers</TocLink>
                </div>
                <TocLink id="permissions">Permissions Reference</TocLink>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
